// Vorschau des Lernmodus wie renderer/src/scene_learn.rs und learn_panel.rs: Bewertungsbalken,
// Brett mit Pfeil für den besseren Zug, Tafel mit Eröffnung, Theorie, Bewertung und Urteil.
import { h, type VNode } from 'snabbdom';
import type { GameDetail, LessonPly, WallpaperSettings } from '../api';
import { t } from '../i18n';
import { ASPECT } from '../wpmath';

const JUDGED = { inaccuracy: ['Ungenauigkeit', '?!'], mistake: ['Fehler', '?'], blunder: ['Grober Fehler', '??'] } as const;
const ROLE: Record<string, string> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };
const idx = (sq: string) => (8 - +sq[1]) * 8 + (sq.charCodeAt(0) - 97);
const at = (i: number) => ({ left: `${(i % 8) * 12.5}%`, top: `${Math.floor(i / 8) * 12.5}%` });
const label = (ply: number, san: string) => `${Math.ceil(ply / 2)}${ply % 2 ? '.' : '…'} ${san}`;

/** Halbzug, mit dem die Partie die benannten Linien verlassen hat, oder −1 */
export function theoryExit(lesson: LessonPly[], ply: number): number {
  const e = lesson.findIndex(l => l.exit);
  return e >= 0 && e <= ply ? e : -1;
}

/** Gewinnchance aus Sicht von Weiß wie lichess; null ohne Bewertung */
export function chances(l: LessonPly, ply: number): number | null {
  if (l.mate === 0) return ply % 2 ? 1 : -1;
  if (l.mate != null) return Math.sign(l.mate);
  if (l.cp == null) return null;
  return 2 / (1 + Math.exp(-0.00368208 * Math.max(-1000, Math.min(1000, l.cp)))) - 1;
}

export function scoreText(l: LessonPly, lang: string): string | null {
  if (l.mate != null) return `#${Math.abs(l.mate)}`;
  if (l.cp == null) return null;
  const s = `${l.cp >= 0 ? '+' : '−'}${Math.abs(l.cp / 100).toFixed(1)}`;
  return lang === 'en' ? s : s.replace('.', ',');
}

function board(fen: string, size: number, arrow: string | null): VNode {
  const kids: VNode[] = [];
  let i = 0;
  for (const ch of fen.split(' ')[0].replace(/\//g, '')) {
    if (/\d/.test(ch)) i += +ch;
    else kids.push(h(`piece.${ch === ch.toUpperCase() ? 'white' : 'black'}.${ROLE[ch.toLowerCase()]}`, { style: at(i++) }));
  }
  if (arrow) {
    const [a, b] = [idx(arrow.slice(0, 2)), idx(arrow.slice(2, 4))];
    const [x1, y1, x2, y2] = [(a % 8) + 0.5, Math.floor(a / 8) + 0.5, (b % 8) + 0.5, Math.floor(b / 8) + 0.5];
    const k = 0.32 / Math.hypot(x2 - x1, y2 - y1);
    kids.push(h('svg.sc-trail', { attrs: { viewBox: '0 0 8 8' } }, [
      h('defs', h('marker#lm-ah', { attrs: { viewBox: '0 0 4 4', refX: 2, refY: 2, markerWidth: 2.2, markerHeight: 2.2, orient: 'auto' } }, h('path', { attrs: { d: 'M0,0 L4,2 L0,4 z', fill: '#15781B' } }))),
      h('line', { attrs: { x1, y1, x2: x2 - (x2 - x1) * k, y2: y2 - (y2 - y1) * k, stroke: '#15781B', 'stroke-opacity': 0.8, 'stroke-width': 0.16, 'stroke-linecap': 'round', 'marker-end': 'url(#lm-ah)' } }),
    ]));
  }
  return h('div.cg-wrap.mini', { style: { width: `${size}cqh`, height: `${size}cqh` } }, kids);
}

export function learnPreview(s: WallpaperSettings, g: GameDetail, lesson: LessonPly[], ply: number, pieces: string): VNode {
  const p = Math.min(ply, g.plies.length - 1, Math.max(0, lesson.length - 1));
  const l = lesson[p], san = g.plies[p].san ?? '';
  const bs = Math.min(s.boardSize * 100, 94, (ASPECT * 94 - 30) / 1.19), px = Math.max(bs * 0.028, 1.1);
  const exit = theoryExit(lesson, p), ex = lesson[exit];
  const j = l?.judgement ? JUDGED[l.judgement] : null;
  const score = l ? scoreText(l, s.lang) : null;
  const surname = (n: string | null) => (n ?? '?').split(',')[0];
  const name = l?.name ?? t('Grundstellung');
  const [family, variation] = name.includes(': ') ? [name.slice(0, name.indexOf(': ')), name.slice(name.indexOf(': ') + 2)] : [name, null];
  const who = s.showNames ? `${surname(g.game.white)} – ${surname(g.game.black)}${s.showYear && g.game.year ? ` · ${g.game.year}` : ''}` : '';
  // wie learn_panel.rs: Kopfzeile, Eröffnung (Familie groß, Variante kleiner), Theorie, Strich, Bewertung, Urteil
  // wie scene_learn.rs: Balken dicht am Brett, Tafel mit mehr Luft
  const panel = h('div.lm-panel', { style: { width: `${Math.max(bs * 0.8, px * 30)}cqh`, fontSize: `${px}cqh`, marginLeft: `${Math.max(bs * 0.14, 3.5) - Math.max(bs * 0.05, 1.2)}cqh` } }, [
    h('div.kicker', [l?.eco ? h('span.eco', l.eco) : null, who ? h('span.who', who) : null]),
    h('div.family', family),
    variation ? h('div.variation', variation) : null,
    exit < 0
      ? h('div.theory', [h('i.dot'), t('Theorie: Stellung steht in der Eröffnungsliste')])
      : h('div.theory', [h('i.dot.off'), t('Theorie verlassen mit {m}', { m: label(exit, g.plies[exit].san ?? '') }),
          h('small', ex.bookMoves.length ? t('Liste geht weiter mit {m}', { m: label(exit, ex.bookMoves.join(' / ')) }) : t('hier endet die benannte Linie'))]),
    h('i.rule'),
    h('div.score', score ? [h('span.n', score), p ? h('span.mv', label(p, san) + (j ? j[1] : '')) : null] : [h('span.pending', t('Bewertung läuft noch'))]),
    j ? h('div.verdict', [h(`span.${l.judgement}`, `${t(j[0])} · ${label(p, san)}${j[1]}`), l.better ? h('small', t('besser laut Stockfish: {m}', { m: l.better })) : null]) : null,
  ]);
  const white = l ? chances(l, p) : null;
  return h(`div.desk.scene.set-${pieces}.theme-${s.theme}.m-learn`, { style: { '--dim': String(s.brightness) } as any },
    h(`div.sc-stage.${s.position}`, h('div.sc-row.lm-row', { style: { gap: `${Math.max(bs * 0.05, 1.2)}cqh` } }, [
      h('div.lm-bar', { style: { height: `${bs}cqh`, width: `${Math.max(bs * 0.019, 0.4)}cqh` } }, white == null ? [] : [h('i', { style: { height: `${50 + 50 * white}%` } })]),
      board(g.plies[p].fen, bs, l?.arrow ?? null),
      panel,
    ])));
}
